import React, { ReactNode } from "react";
import gql from "graphql-tag";
import { formatDistance } from "date-fns";
import styled from "@emotion/styled";
import { LocationContext } from "@reach/router";
import { useStoreAppValue } from "@hooks";
import { prettyUrl, renderIf } from "@services";
import { Box, Image, Text, H2, FontAwesome, TouchableOpacity } from "@atoms";
import { LinkType } from "@types";
import BuyButton from "../BuyButton/BuyButton";
import { LinkModalHeroFragment } from "./__generated__/LinkModalHeroFragment";

// == Types ================================================================

interface IProps {
  children: ReactNode;
  link: LinkModalHeroFragment;
  campaignId?: string;
  fundraiserId?: string;
  onLinkClick: () => void;
  locationContext: LocationContext;
}

// == Constants ============================================================

LinkModalHero.fragment = gql`
  fragment LinkModalHeroFragment on Link {
    id
    type
    image
    name
    availableDenominations
    giveSentence
    promoSecondaryLabel
    promoEndsAt
    cashbackTiming
    defaultBrand {
      id
    }
    merchant {
      id
      website
    }
    ...BuyButtonFragment
  }
  ${BuyButton.fragment}
`;

const LOGO_WIDTH = 200;

// == Component ============================================================

export default function LinkModalHero({
  link,
  locationContext,
  campaignId,
  fundraiserId,
  onLinkClick,
  children,
}: IProps) {
  const { incentiveAvailable, incentiveAmount } = useStoreAppValue();
  const { location, navigate } = locationContext;
  const { tinyLinkKey } = location.state;

  const onBackClick = () => {
    if (location.state.canReturn) {
      window.history.back();
    } else {
      navigate(location.pathname);
    }
  };

  const { merchant } = link;
  const isGiftcard = link.type === LinkType.GIFTCARDOFFER;

  return (
    <Box p={3} textAlign="center">
      <Box>
        <TouchableOpacity onTouch={onBackClick}>
          <Text color="primary">
            <FontAwesome icon="arrow-left" />
          </Text>
        </TouchableOpacity>
      </Box>
      {!!link.image && (
        <Image
          alt={link.name}
          borderRadius={isGiftcard ? "giftcard" : 0}
          boxShadow="lightShadow"
          maxWidth={LOGO_WIDTH}
          mb={2}
          mx="auto"
          src={`${link.image}?w=${LOGO_WIDTH}`}
        />
      )}
      <Box mb={2} textAlign="center">
        <H2 textAlign="center">{link.name}</H2>
        {!isGiftcard && !!merchant && !!merchant.website && (
          <Text display="block" fontSize={0}>
            At {prettyUrl(merchant.website).toLowerCase()}
          </Text>
        )}
        {renderIf(isGiftcard && !!link.availableDenominations)(
          <Text display="block" fontSize={0}>
            {link.availableDenominations}
          </Text>
        )}
      </Box>
      <Box mb={1} textAlign="center">
        <Text display="block" lineHeight="1" textAlign="center" textStyle="givesText">
          {link.giveSentence}
          {incentiveAvailable &&
            !!incentiveAmount &&
            ` +$${parseFloat(incentiveAmount).toFixed()} First Shop Bonus`}
        </Text>
        {renderIf(!!link.promoSecondaryLabel || !!link.promoEndsAt)(
          <Box textAlign="center">
            {!!link.promoSecondaryLabel && (
              <EPromoLabelText
                as="div"
                color="default"
                dangerouslySetInnerHTML={{ __html: link.promoSecondaryLabel }}
              />
            )}
            {!!link.promoEndsAt && (
              <Text color="primary" display="block" textStyle="blockText">
                Ends in {formatDistance(new Date(link.promoEndsAt), new Date())}
              </Text>
            )}
          </Box>
        )}
        <Text
          display="block"
          fontSize={0}
          fontWeight="bold"
          mt={1}
          textAlign="center"
        >{`Gives back ${link.cashbackTiming}`}</Text>
      </Box>
      {children}
      {/* ONLY FOR SHOP */}
      <BuyButton
        campaignId={campaignId}
        fundraiserId={fundraiserId}
        link={link}
        tinyLinkKey={tinyLinkKey}
        onLinkClick={onLinkClick}
      />
    </Box>
  );
}

// == Styles ===============================================================

const EPromoLabelText = styled(Text)`
  text-align: center;
  color: ${(props) => props.theme.colors.primary};
  font-size: ${(props) => props.theme.fontSizes[0]}px;
  * {
    color: ${(props) => props.theme.colors.primary};
    font-size: ${(props) => props.theme.fontSizes[0]}px;
  }
`;
